import Component from '@glimmer/component';
import Select, { type SelectOption } from 'tio-ui/components/select';
import { PhDatabase, PhMagnifyingGlass } from 'ember-phosphor-icons';
import { AVAILABLE_DATASETS, sqlDatasets } from '../utils/dataset-utils';

interface DatasetSelectorSignature {
  Args: {
    selectedDataset: string;
    onDatasetChange: (dataset: string) => void;
    disabled?: boolean;
    /**
     * Opt-in for RFC 062 SQL datasets. The module registry outlives the
     * route that fills it, so surfaces that can't query SQL datasets
     * (tas-programs pages) must not list them — a selection saved with a
     * SQL slug would silently fall back to the Person index on a fresh
     * session. The report and dashboard builders both opt in.
     */
    includeSqlDatasets?: boolean;
    /**
     * Set when the host surface has no company scope: param-kind SQL
     * datasets (one-company-per-request, RFC 062) are listed but disabled
     * so an unqueryable selection can't be made. Cross-company-capable
     * SQL datasets stay selectable.
     */
    disableSqlWithoutCompany?: boolean;
  };
}

export default class DatasetSelector extends Component<DatasetSelectorSignature> {
  // SQL datasets appear alongside the OpenSearch ones, differentiated by
  // icon + group header — source kind is a real routing difference (RFC
  // 062: /api/v2/search vs /api/v2/sql-search). Empty unless the app
  // registered published SQL datasets.
  get datasetOptions(): SelectOption[] {
    const sql = this.args.includeSqlDatasets ? sqlDatasets() : [];
    // Group headers only earn their rows when both kinds are present.
    const grouped = sql.length > 0;
    const openSearch = AVAILABLE_DATASETS.map((dataset) => ({
      key: dataset.key,
      label: dataset.label,
      icon: PhMagnifyingGlass,
      ...(grouped ? { group: 'OpenSearch datasets' } : {}),
    }));
    const sqlOptions = sql.map((dataset) => {
      const disabled = Boolean(this.args.disableSqlWithoutCompany && !dataset.crossCompanyCapable);
      return {
        key: dataset.key,
        label: dataset.label,
        icon: PhDatabase,
        group: 'SQL datasets',
        ...(disabled ? { disabled, description: 'Requires a company scope' } : {}),
      };
    });
    return [...openSearch, ...sqlOptions];
  }

  <template>
    <div class="shrink-0 min-w-[200px]" data-test-dataset-selector>
      <Select
        @items={{this.datasetOptions}}
        @selected={{@selectedDataset}}
        @onChange={{@onDatasetChange}}
        @size="sm"
        @placeholder="Select dataset"
      />
    </div>
  </template>
}
